import React, { type FC } from 'react';
import { FormattedMessage, type MessageDescriptor } from 'react-intl-next';

import { Stack, Text, Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { ConfluenceEdition } from '@confluence/session-data';
import { ScreenEvent } from '@confluence/analytics';

import {
	AUTO_UPGRADE_AWARENESS_MODAL_SOURCE,
	AUTO_UPGRADE_AWARENESS_STORAGE_USAGE_COMPONENT,
} from '../hooks/analytics-handlers/analyticsConstants';
import { useGetStorageUsagePercentage } from '../hooks/useGetStorageUsagePercentage';

type SubHeadingProps = {
	edition: ConfluenceEdition | null;
	text: { [index: string]: MessageDescriptor };
	storageUsage: number;
};

type StorageHeaderProps = {
	text: MessageDescriptor;
	storageUsage: number;
	edition?: ConfluenceEdition | null;
};

type StorageUsageProgressBarProps = {
	storageUsagePercentage?: number;
};

const FREE_TIER_STORAGE_LIMIT_GB = 2;

const storageUsageProgressBarOuterStyles = xcss({
	marginTop: 'space.200',
	borderRadius: token('space.200'),
});
const storageUsageProgressBarInnerStyles = xcss({
	borderRadius: token('border.radius.circle'),
});

const StorageUsageSectionHeader: FC<StorageHeaderProps> = ({ text, storageUsage }) => {
	return (
		<Text color="color.text" size="large" weight="medium">
			<FormattedMessage {...text} values={{ dataUsageinGB: storageUsage }} />
		</Text>
	);
};

const StorageUsageSectionSubHeader: FC<StorageHeaderProps> = ({ edition, text, storageUsage }) => {
	return (
		<Text color="color.text.subtlest" size="small">
			{edition === ConfluenceEdition.FREE ? (
				<FormattedMessage {...text} values={{ dataUsageinGB: storageUsage }} />
			) : (
				<FormattedMessage
					{...text}
					values={{ dataUsageOverFreeLimitInGB: storageUsage - FREE_TIER_STORAGE_LIMIT_GB }}
				/>
			)}
		</Text>
	);
};

const StorageUsageProgressBar: FC<StorageUsageProgressBarProps> = ({ storageUsagePercentage }) => {
	return (
		<Box
			xcss={storageUsageProgressBarOuterStyles}
			backgroundColor="color.background.accent.gray.subtlest"
		>
			<Box
				xcss={storageUsageProgressBarInnerStyles}
				style={{ width: `${storageUsagePercentage}%` }}
				padding="space.100"
				backgroundColor="color.background.accent.blue.bolder"
			/>
		</Box>
	);
};

export const StorageUsageSection: FC<SubHeadingProps> = ({ edition, text, storageUsage }) => {
	const { usagePercentage, canRenderStorageUsageBar } = useGetStorageUsagePercentage({
		edition,
		storageUsage,
	});

	if (!canRenderStorageUsageBar) {
		return null;
	}

	return (
		<Stack testId="storage-usage-section">
			<ScreenEvent
				name={AUTO_UPGRADE_AWARENESS_STORAGE_USAGE_COMPONENT}
				id={AUTO_UPGRADE_AWARENESS_MODAL_SOURCE}
				attributes={{
					dataUsed: storageUsage,
					edition,
				}}
			/>
			<StorageUsageSectionHeader text={text.header} storageUsage={storageUsage} />
			<StorageUsageSectionSubHeader
				text={text.subHeader}
				storageUsage={storageUsage}
				edition={edition}
			/>
			<StorageUsageProgressBar storageUsagePercentage={usagePercentage} />
		</Stack>
	);
};
